import React, { useRef, useState, useEffect, useMemo } from 'react'
import useAuth from '../../hooks/useAuth'
import Modal from '../../components/Modal'
import { Link, useNavigate, useLocation, useFetcher } from 'react-router-dom'
import useAxiosPrivate from '../../hooks/useAxiosPrivate'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { NavLink } from 'react-router-dom'
import {
  faArrowRight,
  faCheck,
  faChevronRight,
  faEnvelope,
  faPhone,
} from '@fortawesome/free-solid-svg-icons'
import Contact from '../../components/Contact'
import { useTheme } from '../../context/ThemeProvider'
import StaffWellbeingPortalPromo from '../../components/StaffWellbeingPortalPromo'

const STAFF_PORTAL_CODE_STORAGE_PREFIX =
  'staff_wellbeing_portal_access_code_'

const getStaffPortalAccessCodeFromObject = (obj) => {
  if (!obj || typeof obj !== 'object') return ''

  const candidateKeys = [
    'staff_portal_access_code',
    'staffPortalAccessCode',
    'staff_portal_code',
    'staffPortalCode',
    'portal_access_code',
    'portalAccessCode',
    'portal_code',
    'portalCode',
    'swb_portal_code',
    'swbPortalCode',
    'wellbeing_portal_code',
    'wellbeingPortalCode',
    'access_code',
    'accessCode',
    'code',
  ]

  for (const key of candidateKeys) {
    const value = obj?.[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
    if (typeof value === 'number' && String(value).trim()) return String(value)
  }

  return ''
}

const safeLocalStorageGet = (key) => {
  try {
    return localStorage.getItem(key)
  } catch (e) {
    return null
  }
}

const safeLocalStorageSet = (key, value) => {
  try {
    localStorage.setItem(key, value)
  } catch (e) {
    // ignore
  }
}

const Dashboard = () => {
  const { auth, setAuth, setShowLoader, createInfo, mainDpt, setMainDpt } =
    useAuth()
  const axios = useAxiosPrivate()
  const theme = useTheme()

  const style = {
    backgroundColor: theme.backgroundColor,
    color: theme.textColor,
  }

  const [currentOrg, setCurrentOrg] = useState({})
  const [user, setUser] = useState([])
  const [loading, setLoading] = useState(true)
  const [token, setToken] = useState('')
  const [saiPolicy, setSaiPolicy] = useState([])
  const [srmPolicy, setSrmPolicy] = useState([])
  const [swbPolicy, setSWBPolicy] = useState([])
  const [ohPolicy, setOHPolicy] = useState([])
  const [loadedPolicies, setLoadedPolicies] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [modalTitle, setModalTitle] = useState({})
  const navigate = useNavigate()
  const location = useLocation()

  const handleClick = (dpt, contact, policy = null) => {
    let url
    switch (dpt) {
      case 'SWB':
        if (contact.wbo !== 1 || policy.product === 'SWB OH+') {
          return
        }
        url = `${process.env.REACT_APP_WBO}/authenticate-token?token=${token}`
        window.location.href = url
        break
      case 'OH':
        if (contact.oh !== 1) {
          return
        }

        setAuth({ ...auth, currentPolicy: policy })
        localStorage.setItem('currentPolicy', JSON.stringify(policy))
        navigate('/oh/dashboard')

        break

      case 'SAI':
        if (contact.sai !== 1 || !policy?.department) {
          return
        }
        url = `${process.env.REACT_APP_SAS}/authenticate-token?token=${token}`
        if (policy?.department === 'Mutual') {
          url = `${process.env.REACT_APP_MUTUAL}/authenticate-token?token=${token}`
        }
        if (policy?.department === 'SRM') {
          url = `${process.env.REACT_APP_SRM}/authenticate-token?token=${token}`
        }
        if (policy?.department === 'CC') {
          url = `${process.env.REACT_APP_CC}/authenticate-token?token=${token}`
        }
        window.location.href = url
        break
      default:
        break
    }
    // window.location.href = url
  }

  const openLogoutModal = () => {
    setModalTitle(`Your details could not be verified`)
    setShowModal(true)
  }

  const ModalBody = () => {
    if (showModal) {
      return <NoPolicyFoundElement />
    }

    return null
  }

  const NoPolicyFoundElement = () => {
    const redirectToLogin = async (e) => {
      window.location.reload()
    }

    return (
      <form>
        <div className="mb-3 py-4 text-center">
          We could not verify your details. <br /> Please contact your account
          manager.
        </div>
        <div className="flex mt-2 w-full">
          <button
            type="button"
            className="btn red mt-4 w-full"
            onClick={redirectToLogin}
          >
            Close
          </button>
        </div>
      </form>
    )
  }

  const resetModal = (resetNotes = true) => {
    setShowModal(false)
  }

  const PolicyStatus = ({ status }) => {
    let bg = ''
    let text = ''
    let title = ''
    let dot = ''
    switch (status) {
      case 'live':
        bg = 'bg-green-200'
        dot = 'bg-green-600'
        text = 'text-green-600'
        title = 'Active'
        break
      case 'previous':
        bg = 'bg-rose-300/30'
        dot = 'bg-rose-600/50'
        text = 'text-rose-600/50'
        title = 'Expired'
        break
      case 'expiring':
        bg = 'bg-amber-300/30'
        text = 'text-amber-600/60'
        dot = 'bg-amber-600/60'
        title = 'Expiring'
        break
      case 'accepted':
        bg = 'bg-amber-300/30'
        text = 'text-amber-600/60'
        dot = 'bg-amber-600/60'
        title = 'Pending'
        break
      default:
    }

    return (
      <div
        className={`rounded ${bg} w-1/2 p-2.5 flex justify-center items-center ${text} text-base`}
      >
        <span className={`w-2 h-2 block rounded-full ${dot} mr-3`}>&nbsp;</span>
        <span className="font-bold">{title}</span>
      </div>
    )
  }

  const Policy = ({ dpt, policy }) => {
    let text = ''
    switch (dpt) {
      case 'sai':
        text = `Manage your staff details, holidays ${policy.department !== 'Mutual' && policy.department !== 'GBS'
          ? 'and absence claims'
          : 'and payment requests'
          }.`
        break
      case 'swb':
        text = 'Click here to manage your Standalone Wellbeing Agreement'
        break
      case 'oh':
        text =
          'Click here to create and manage your Occupational Health Requests'
        break
      default:
        break
    }
    return (
      <>
        <p className="text-center text-base text-[#201E5F]/80 mt-4 lg:min-h-[100px]">
          {text}
        </p>
        <div className="flex justify-between mt-8 space-x-4">
          <div className="rounded bg-purple-300/30 w-1/2 p-2.5 text-sm flex justify-center items-center text-purple-800/50 xl:text-base font-bold">
            {policy?.policy_reference}
          </div>
          <PolicyStatus status={policy.policy_status} />
        </div>
      </>
    )
  }

  const NotAllowed = ({ dpt }) => {
    return (
      <>
        <p className="text-center text-base text-[#201E5F]/80 mt-4 lg:min-h-[100px]">
          You currently don't have access to this product. Please contact the
          main contact at your school/trust if you believe this is an error.
        </p>
        <div className="flex justify-between mt-8">
          <div className="rounded bg-rose-300/60 w-full text-sm p-2.5 flex justify-center items-center text-rose-600/60 xl:text-base font-bold cursor-not-allowed">
            You have no access to this product
          </div>
        </div>
      </>
    )
  }

  const PolicyOh = () => {
    return (
      <>
        <p className="text-center text-base text-[#201E5F]/80 mt-4 lg:min-h-[100px]">
          Click here to create and manage your Occupational Health Requests
        </p>
        <div className="flex justify-center mt-8">
          <div
            className={`rounded bg-green-200 w-full p-2.5 flex justify-center items-center text-green-600 text-base`}
          >
            <span className="font-bold">Active</span>
          </div>
        </div>
      </>
    )
  }

  const NoPolicy = ({ dpt }) => {
    let text = ''
    switch (dpt) {
      case 'sai':
        text = "You currently don't have a staff absence cover."
        break
      case 'swb':
        text =
          'You do not have a subscription to one of our separate, standalone Wellbeing Schemes.'
        break
      case 'oh':
        text =
          'You do not have a subscription for our extended Occupational Health services.'
        break
      default:
        break
    }
    return (
      <>
        <p className="text-center text-base text-[#201E5F]/80 mt-4 lg:min-h-[100px]">
          {text}
        </p>
        <div className="flex justify-between">
          <p className="w-full text-sm p-2.5 text-center xl:text-base">
            Please contact your Account Manager to discuss any additional{' '}
            {dpt === 'oh' && 'Occupational Health'}
            {dpt === 'sai' && 'Staff Absence'}
            {dpt === 'swb' && 'Wellbeing'} requirements.
          </p>
        </div>
      </>
    )
  }

  const ShowSAIButton = ({ type, policy }) => {
    switch (type) {
      case 'policy':
        return <Policy dpt="sai" policy={policy} />
      case 'not-allowed':
        return <NotAllowed dpt="sai" />
      case 'no-policy':
        return <NoPolicy dpt="sai" />
      default:
        break
    }
  }
  const ShowSRMButton = ({ type, policy }) => {
    switch (type) {
      case 'policy':
        return <Policy dpt="srm" policy={policy} />
      case 'not-allowed':
        return <NotAllowed dpt="srm" />
      case 'no-policy':
        return <NoPolicy dpt="srm" />
      default:
        break
    }
  }
  const ShowSWBButton = ({ type, policy }) => {
    switch (type) {
      case 'policy':
        return <Policy dpt="swb" policy={policy} />
      case 'not-allowed':
        return <NotAllowed dpt="swb" />
      case 'no-policy':
        return <NoPolicy dpt="swb" />
      default:
        break
    }
  }
  const ShowOHButton = ({ type, policy }) => {
    if (!auth?.user?.oh) {
      return <NotAllowed dpt="oh" />
    } else if (ohPolicy) {
      return <PolicyOh policy={ohPolicy} />
    } else if (
      saiPolicy &&
      (saiPolicy.department === 'Mutual' ||
        saiPolicy.department === 'SAS' ||
        saiPolicy.department === 'SRM')
    ) {
      return <PolicyOh policy={saiPolicy} />
    } else {
      return <NoPolicy dpt="oh" />
    }
  }

  const getPolicys = async () => {
    setShowLoader(true)
    const response = await axios.post('/staff-hub/policy', {
      token: auth.token,
      orgId: auth.schools[0].id ?? auth?.schools[0].id,
      user: auth?.user?.contact_id,
    })

    if (response.data.error === true) {
      setShowLoader(false)
      setLoading(false)
      openLogoutModal()
    } else {
      setSaiPolicy(response.data.result?.data.policies?.sai)
      setCurrentOrg(response.data.result?.data?.org || {})
      if (response.data.result?.data.policies?.sai) {
        setMainDpt(response.data.result?.data.policies?.sai.department)
      } else {
        setMainDpt('SAS')
      }
      setSrmPolicy(response.data.result?.data.policies?.srm)
      if (response.data.result?.data.policies?.srm) {
        setMainDpt(response.data.result?.data.policies?.srm.department)
      }

      setSWBPolicy(response.data.result?.data.policies?.swb)
      setOHPolicy(response.data.result?.data.policies?.oh)
      setToken(response.data.result?.data?.token)
      setLoading(false)

      const orgId = auth?.schools?.[0]?.id
      if (orgId) {
        const orgCode = getStaffPortalAccessCodeFromObject(
          response.data.result?.data?.org
        )
        const schoolCode = getStaffPortalAccessCodeFromObject(
          auth?.schools?.[0]
        )
        const swbPolicyCode = getStaffPortalAccessCodeFromObject(
          response.data.result?.data?.policies?.swb
        )
        const ohPolicyCode = getStaffPortalAccessCodeFromObject(
          response.data.result?.data?.policies?.oh
        )
        const saiPolicyCode = getStaffPortalAccessCodeFromObject(
          response.data.result?.data?.policies?.sai
        )

        const accessCode =
          orgCode || schoolCode || swbPolicyCode || ohPolicyCode || saiPolicyCode
        if (accessCode) {
          safeLocalStorageSet(
            `${STAFF_PORTAL_CODE_STORAGE_PREFIX}${orgId}`,
            accessCode
          )
        }
      }

      localStorage.setItem(
        'orgType',
        JSON.stringify(
          response.data.result.data.org.trust_name
            ? {
              type: 'trust',
              postcode: response.data.result.data.org.trust_postcode,
              name: response.data.result.data.org.trust_name,
            }
            : {
              type: 'academy',
              postcode: response.data.result.data.org.academy_postcode,
              name: response.data.result.data.org.academy_name,
            }
        )
      )
    }

    setShowLoader(false)
  }

  useEffect(() => {
    if (!loadedPolicies) {
      setLoadedPolicies(true)
      getPolicys()
      localStorage.removeItem('currentPolicy')
    }
  }, [auth])

  useEffect(() => {
    if (mainDpt === 'CC') {
      handleClick('SAI', auth.user, saiPolicy)
    }
  }, [mainDpt])

  const staffWellbeingPortalAccessCode = useMemo(() => {
    const school = auth?.schools?.[0] || {}
    console.log(school);
    const orgId = school?.id
    const cachedCode = orgId
      ? safeLocalStorageGet(`${STAFF_PORTAL_CODE_STORAGE_PREFIX}${orgId}`)
      : null

    return (
      getStaffPortalAccessCodeFromObject(school) ||
      getStaffPortalAccessCodeFromObject(currentOrg) ||
      getStaffPortalAccessCodeFromObject(swbPolicy) ||
      getStaffPortalAccessCodeFromObject(ohPolicy) ||
      getStaffPortalAccessCodeFromObject(saiPolicy) ||
      cachedCode ||
      ''
    )
  }, [auth?.schools, currentOrg, swbPolicy, ohPolicy, saiPolicy])

  const staffWellbeingPortalPopupStorageKey = useMemo(() => {
    const orgId = auth?.schools?.[0]?.id
    if (!orgId) return null
    return `staff_wellbeing_portal_launch_popup_dismissed_v1_${orgId}`
  }, [auth?.schools])

  return (
    <>
      {showModal && (
        <Modal title={modalTitle} body={<ModalBody />} show={resetModal} />
      )}
      <div className="mb-8 sm:mb-16">
        <h3 className="text-2xl sm:text-3xl md:text-5xl font-bold text-white text-center pt-12 ">
          Welcome Back, {auth?.user?.contact_name}
        </h3>
        <h4 className="text-lg font-medium text-white text-center">
          You are now viewing {auth?.schools[0]?.name}, which product would you
          like to view?
        </h4>
      </div>

      <div className="w-full max-w-7xl flex-col lg:flex-row flex space-y-10 lg:space-y-0 lg:space-x-5 xl:space-x-10">
        {!srmPolicy && (
          <div
            className={`w-full lg:w-1/3 bg-white shadow-lg rounded-md overflow-hidden flex flex-col-reverse lg:flex-row-reverse ${auth?.user?.sai === 1 && saiPolicy
              ? ' cursor-pointer'
              : ' cursor-not-allowed'
              }`}
            onClick={() => {
              saiPolicy && handleClick('SAI', auth.user, saiPolicy)
            }}
          >
            <div className="w-full p-8 lg:p-6 xl:p-8 min-h-[250px]">
              <h3 className="text-center text-xl sm:text-2xl lg:text-xl text-[#201E5F] font-semibold">
                {loading ? (
                  'Loading...'
                ) : (
                  <>
                    {saiPolicy?.department === 'GBS' ||
                      saiPolicy?.department === 'Mutual'
                      ? 'Staff Absence Agreement'
                      : 'Staff Absence Policy'}
                  </>
                )}
              </h3>
              {loading ? null : (
                <ShowSAIButton
                  type={
                    auth?.user?.sai
                      ? saiPolicy
                        ? 'policy'
                        : 'no-policy'
                      : 'not-allowed'
                  }
                  policy={saiPolicy}
                />
              )}
            </div>
          </div>
        )}
        {/* 
        {!saiPolicy && (
          <div
            className={`w-full lg:w-1/3 bg-white shadow-lg rounded-md overflow-hidden flex flex-col-reverse lg:flex-row-reverse ${
              auth?.user?.sai === 1 && srmPolicy
                ? ' cursor-pointer'
                : ' cursor-not-allowed'
            }`}
            onClick={() => {
              srmPolicy && handleClick('SRM', auth.user, srmPolicy)
            }}
          >
            <div className="w-full p-8 lg:p-6 xl:p-8 min-h-[250px]">
              <h3 className="text-center text-xl sm:text-2xl lg:text-xl text-[#201E5F] font-semibold">
                {loading ? 'Loading...' : 'Staff Absence Agreement'}
              </h3>
              {loading ? null : (
                <ShowSRMButton
                  type={
                    auth?.user?.sai
                      ? srmPolicy
                        ? 'policy'
                        : 'no-policy'
                      : 'not-allowed'
                  }
                  policy={srmPolicy}
                />
              )}
            </div>
          </div>
        )} */}

        <div
          className={`w-full lg:w-1/3 bg-white shadow-lg rounded-md overflow-hidden flex flex-col-reverse lg:flex-row-reverse ${auth?.user?.wbo === 1 &&
            swbPolicy &&
            swbPolicy?.product !== 'SWB OH+'
            ? ' cursor-pointer'
            : ' cursor-not-allowed'
            }`}
          onClick={() => {
            swbPolicy && handleClick('SWB', auth.user, swbPolicy)
          }}
        >
          <div className="w-full p-8 lg:p-6 xl:p-8 min-h-[250px]">
            <h3 className="text-center text-xl sm:text-2xl lg:text-xl text-[#201E5F] font-semibold">
              {loading
                ? 'Loading...'
                : mainDpt === 'CC'
                  ? 'Coming Soon...'
                  : 'Whole School Wellbeing'}
            </h3>
            {loading ? null : (
              <ShowSWBButton
                type={
                  auth?.user?.wbo
                    ? swbPolicy?.product === 'SWB OH+'
                      ? 'not-allowed'
                      : swbPolicy
                        ? 'policy'
                        : 'no-policy'
                    : 'not-allowed'
                }
                policy={swbPolicy}
              />
            )}
          </div>
        </div>

        <div
          className={`w-full lg:w-1/3 bg-white shadow-lg rounded-md overflow-hidden flex flex-col-reverse lg:flex-row-reverse ${auth?.user?.oh === 1 && (ohPolicy || saiPolicy)
            ? ' cursor-pointer'
            : ' cursor-not-allowed'
            }`}
          onClick={() => {
            ohPolicy
              ? handleClick('OH', auth.user, ohPolicy)
              : saiPolicy && handleClick('OH', auth.user, saiPolicy)
          }}
        >
          <div
            className={`w-full p-8 lg:p-6 min-h-[250px] xl:p-8 ${auth?.user?.oh === 1 && (ohPolicy || saiPolicy)
              ? ' cursor-pointer'
              : ' cursor-not-allowed'
              }`}
          >
            <h3 className="text-center text-2xl lg:text-xl text-[#201E5F] font-semibold">
              {loading
                ? 'Loading...'
                : mainDpt === 'CC'
                  ? 'Coming Soon...'
                  : 'Occupational Health'}
            </h3>
            {loading ? null : (
              <ShowOHButton
                type={
                  auth?.user?.oh
                    ? ohPolicy
                      ? 'policy'
                      : 'no-policy'
                    : 'not-allowed'
                }
                policy={ohPolicy}
              />
            )}
          </div>
        </div>
      </div>

      <StaffWellbeingPortalPromo
        accessCode={staffWellbeingPortalAccessCode}
        storageKey={staffWellbeingPortalPopupStorageKey}
      />

      <Contact />
    </>
  )
}

export default Dashboard
